import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Gift, Send } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Money } from "@/components/common/Money";
import { useCurrentPool, useLeaderboard } from "@/hooks/useGift";
import { InitialsAvatar } from "@/components/common/InitialsAvatar";
import { GIFT_TOP_RANK } from "@/lib/constants";
import { formatRank, formatFcfa } from "@/lib/format";
import { supabase, callFunction } from "@/lib/supabase";
import { startOfWeek, format } from "date-fns";
import { toast } from "sonner";

export function AdminGiftPool() {
  const { t } = useTranslation();
  const pool = useCurrentPool();
  const lb = useLeaderboard(GIFT_TOP_RANK);
  const qc = useQueryClient();
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    if (pool.data) setAmount(pool.data.total_amount);
  }, [pool.data]);

  const save = useMutation({
    mutationFn: async (val: number) => {
      const weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd");
      const { error } = await supabase
        .from("gift_pools")
        .upsert({ week_start: weekStart, total_amount: val }, { onConflict: "week_start" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gift-pool"] });
      toast.success(t("admin.giftPoolForm.save"));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Erreur"),
  });

  const distribute = useMutation({
    mutationFn: () => callFunction("gift-pool-distribute"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gift-pool"] });
      qc.invalidateQueries({ queryKey: ["gift-leaderboard"] });
      toast.success(t("admin.giftPoolForm.distributed"));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Erreur"),
  });

  return (
    <div className="space-y-4 max-w-2xl">
      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center gap-3">
            <Gift className="size-6 text-gold" />
            <div className="flex-1">
              <h3 className="font-bold">Cagnotte de la semaine</h3>
              <p className="text-sm text-muted-foreground">
                {pool.data?.is_distributed ? "Déjà distribuée" : "En cours"}
              </p>
            </div>
            {pool.data?.is_distributed && <Badge variant="success">Distribuée</Badge>}
          </div>

          <div className="space-y-2">
            <Label>{t("admin.giftPoolForm.amount")}</Label>
            <div className="flex gap-2">
              <Input type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="text-xl font-bold h-12" disabled={pool.data?.is_distributed} />
              <Button onClick={() => save.mutate(amount)} loading={save.isPending} disabled={pool.data?.is_distributed}>
                {t("admin.giftPoolForm.save")}
              </Button>
            </div>
            {amount > 0 && <p className="text-xs text-muted-foreground">= {formatFcfa(amount)}</p>}
          </div>

          {!pool.data?.is_distributed && (
            <Button
              variant="default"
              size="lg"
              className="w-full"
              loading={distribute.isPending}
              onClick={() => {
                if (confirm(t("admin.giftPoolForm.distributeConfirm"))) distribute.mutate();
              }}
              disabled={!amount}
            >
              <Send /> {t("admin.giftPoolForm.distribute")}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <h3 className="font-bold mb-3">Classement TOP {GIFT_TOP_RANK}</h3>
          <div className="space-y-1">
            {(lb.data ?? []).map((row, idx) => {
              const share = amount && lb.data?.length ? Math.floor(amount / lb.data.length) : 0;
              return (
                <div key={row.id} className="flex items-center gap-3 py-1.5">
                  <span className="w-8 text-center font-bold">{formatRank(idx + 1)}</span>
                  <InitialsAvatar name={row.users?.first_name} seed={row.user_id} size="sm" />
                  <span className="flex-1 text-sm">{row.users?.first_name ?? "—"}</span>
                  <span className="text-xs text-muted-foreground">{row.points} pts</span>
                  {share > 0 && <span className="font-bold text-success text-sm"><Money amount={share} compact /></span>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
